import type { AppContext, GrantResult, Store } from '../shared';
import { activityFor, chapterPlan, chapterReady, rewardFor, trainingCost, xpToNext } from './economy';
import { readEconomy, type TrainingStat } from './save';
import { deriveBuild, equipmentProgress, newlyEarnedEquipment, readEquipment } from './equipment';
import { EQUIPMENT, equipmentItem } from '../content/equipment/catalog';
import type { EquipmentMilestone } from '../shared';

/** New gear is awarded once, in the same transaction as the activity reward. Old saves catch up quietly at boot. */
function refreshEquipment(ctx: AppContext, source?: string): string[] {
  const s = readEconomy(ctx.store.state.slices.economy), gear = readEquipment(s.equipment);
  const progress = equipmentProgress(ctx.store.state.slices, gear);
  const milestone: EquipmentMilestone | undefined = source?.startsWith('arena.') ? 'arena' : source?.startsWith('cards.match.') ? 'cards' :
    source === 'explore.chapter' ? 'road' : source === 'dungeon.clear' ? 'crypt' : source === 'order.quest' ? 'order' : undefined;
  if (milestone) progress[milestone] = Math.max(progress[milestone], (gear.milestones[milestone] ?? 0) + 1);
  gear.milestones = progress;
  const earned = newlyEarnedEquipment(ctx.store.state.slices, gear);
  gear.owned.push(...earned);
  if (JSON.stringify(gear) !== JSON.stringify(s.equipment)) ctx.store.update('economy', draft => { draft.equipment = gear; }, 'equipment:milestones');
  return earned;
}

function installEquipment(ctx: AppContext): void {
  refreshEquipment(ctx);
  const current = () => deriveBuild(readEconomy(ctx.store.state.slices.economy));
  ctx.services.equipment = {
    current,
    preview: id => equipmentItem(id) ? deriveBuild(readEconomy(ctx.store.state.slices.economy), id) : null,
    inventory() {
      const gear = readEconomy(ctx.store.state.slices.economy).equipment, progress = equipmentProgress(ctx.store.state.slices, gear);
      return EQUIPMENT.map(item => ({ item: structuredClone(item), owned: gear.owned.includes(item.id), equipped: gear.slots[item.slot] === item.id,
        eligible: progress[item.unlock.milestone] >= item.unlock.count, progress: progress[item.unlock.milestone], target: item.unlock.count }));
    },
    equip(id) {
      const item = equipmentItem(id), s = readEconomy(ctx.store.state.slices.economy);
      if (!item || !s.equipment.owned.includes(id) || s.equipment.slots[item.slot] === id) return false;
      ctx.store.update('economy', d => { const next = readEconomy(d); next.equipment.slots[item.slot] = id; Object.assign(d, next); }, `equipment:equip:${id}`);
      return true;
    },
    claim(id) {
      const item = equipmentItem(id), s = readEconomy(ctx.store.state.slices.economy);
      if (!item || s.equipment.owned.includes(id) || equipmentProgress(ctx.store.state.slices, s.equipment)[item.unlock.milestone] < item.unlock.count) return false;
      ctx.store.update('economy', d => { const next = readEconomy(d); next.equipment.owned.push(id); Object.assign(d, next); }, `equipment:claim:${id}`);
      return true;
    },
    appearance(base, build = current()) {
      const appearance = { ...structuredClone(base), ...(build.outfit ? { outfit: build.outfit } : {}), weapon: build.weapon };
      appearance.boots = build.items.boots === 'traveller-boots' ? 'traveller' : build.items.boots === 'iron-boots' ? 'iron' : build.items.boots === 'quiet-boots' ? 'quiet' : 'worn';
      delete appearance.charm;
      const charm = build.items.charm;
      if (charm === 'reliquary' || charm === 'order-seal' || charm === 'thorn-knot') appearance.charm = charm;
      return appearance;
    },
  };
}

/** All economic mutations stay inside the meta owner. Invalid spending never credits money. */
export function installEconomy(ctx: AppContext): void {
  ctx.store.update('economy', (d) => Object.assign(d, readEconomy(d)), 'economy defaults');
  installEquipment(ctx);
  const sinks = new Map<string, (store: Store, amount: number, ref?: string) => void>();
  ctx.services.economy = {
    level: () => readEconomy(ctx.store.state.slices.economy).level,
    balance(currency) {
      const s = readEconomy(ctx.store.state.slices.economy);
      if (currency === 'gold' || currency === 'xp') return s[currency];
      if (currency.startsWith('training.')) return s.training[currency.slice(9) as TrainingStat] ?? 0;
      return s.currencies[currency] ?? 0;
    },
    spend(currency, amount, reason) {
      if (!Number.isFinite(amount) || amount < 0 || !Number.isInteger(amount)) return false;
      if (this.balance(currency) < amount || currency.startsWith('training.') || currency === 'xp') return false;
      ctx.store.update('economy', (d) => {
        const s = readEconomy(d);
        if (currency === 'gold') s.gold -= amount; else s.currencies[currency] = (s.currencies[currency] ?? 0) - amount;
        Object.assign(d, s);
      }, reason);
      return true;
    },
  };
  ctx.services.rewards = {
    registerSink: (kind, sink) => { sinks.set(kind, sink); },
    grant(source, bundle, options) {
      const before = readEconomy(ctx.store.state.slices.economy);
      const claim = typeof bundle.params?.claimId === 'string' ? `${source}:${bundle.params.claimId}` : null;
      if (claim && before.claims.includes(claim)) return { ledgerIds: [], lines: [], capped: true };
      const { xp, gold } = rewardFor(source, bundle, before.level);
      const id = `${source}:${ctx.clock.wallNow()}:${before.activities.arena + before.activities.road + before.activities.cards}`;
      const result: GrantResult = { ledgerIds: [id], lines: [{ key: 'meta.reward.gain', params: { xp, gold } }] };
      let level = before.level;
      ctx.store.transaction(`reward:${source}`, () => {
        ctx.store.update('economy', (d) => {
          const s = readEconomy(d);
          s.xp += xp; s.gold += gold;
          while (s.xp >= xpToNext(s.level)) { s.xp -= xpToNext(s.level); s.level++; s.gold += 20; }
          level = s.level;
          // An order rewards an earlier deed; it is not another journey.
          const activity = source === 'order.quest' ? null : activityFor(source);
          if (activity) s.activities[activity]++;
          for (const [key, amount] of Object.entries(bundle.currencies ?? {})) {
            if (typeof amount === 'number' && Number.isFinite(amount) && amount > 0) s.currencies[key] = (s.currencies[key] ?? 0) + Math.floor(amount);
          }
          if (claim) s.claims = [...s.claims, claim].slice(-200);
          s.ledger = [{ id, source, xp, gold }, ...s.ledger].slice(0, 20);
          Object.assign(d, s);
        }, `grant:${source}`);
        for (const [key, amount] of Object.entries(bundle.currencies ?? {})) if (typeof amount === 'number' && amount > 0) sinks.get(key)?.(ctx.store, amount);
        for (const item of refreshEquipment(ctx, source)) result.lines.push({ key: `equipment.reward.${item}`, params: {} });
      });
      ctx.bus.emit('meta/granted', { source, ledgerIds: [id] });
      if (level > before.level) {
        const creatureId = options?.creatureId ?? ctx.services.creatures.active()?.id ?? 'hero';
        ctx.bus.emit('meta/levelUp', { creatureId, level });
        ctx.bus.emit('ui/toast', { key: 'meta.reward.level', params: { level }, tone: 'reward' });
      }
      return result;
    },
  };
}

export function buyTraining(ctx: AppContext, stat: TrainingStat): boolean {
  const s = readEconomy(ctx.store.state.slices.economy);
  const cost = trainingCost(s.training[stat]);
  if (s.training[stat] >= 12 || s.gold < cost) return false;
  ctx.store.update('economy', (d) => {
    const next = readEconomy(d); next.gold -= cost; next.training[stat]++; Object.assign(d, next);
  }, `train:${stat}`);
  ctx.bus.emit('ui/toast', { key: 'meta.training.done', params: { stat: ctx.t(`meta.stat.${stat}`) }, tone: 'reward' });
  return true;
}

export function claimChapter(ctx: AppContext): boolean {
  const before = readEconomy(ctx.store.state.slices.economy);
  if (!chapterReady(before)) return false;
  const plan = chapterPlan(before);
  ctx.store.transaction('claim adventure chapter', () => {
    ctx.store.update('economy', (d) => {
      const s = readEconomy(d); s.chapters++; s.gold += plan.gold;
      if (plan.stat) s.training[plan.stat] = Math.min(12, s.training[plan.stat] + 1);
      s.chapterTrack = { v: 1, chapter: s.chapters, consumed: { arena: plan.goals[0].target, road: plan.goals[1].target, cards: plan.goals[2].target } };
      Object.assign(d, s);
    }, 'claim adventure chapter');
    refreshEquipment(ctx);
  });
  ctx.bus.emit('ui/toast', { key: plan.stat ? 'meta.chapter.claimedTraining' : 'meta.chapter.claimed',
    params: { gold: plan.gold, stat: plan.stat ? ctx.t(`meta.stat.${plan.stat}`) : '' }, tone: 'reward' });
  return true;
}
